import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import { imgSrc } from "@/lib/api";
import { FavButton } from "@/components/site/FavButton";
import { Price } from "@/context/CurrencyContext";

export const SampleBadge = ({ show }) =>
  show ? <span className="lab absolute top-3 left-3 z-10 bg-white/90 text-gw-onwhite px-2 py-1" data-testid="sample-badge">Sample</span> : null;

export const PropertyCard = ({ p, large = false, idx }) => (
  <article className={`group ${large ? "md:col-span-2 md:row-span-2" : "md:col-span-2"}`} data-testid={`property-card-${idx}`}>
    <Link to={`/realty/${p.id}`} className="block" data-testid={`property-card-link-${idx}`}>
      <figure className="relative overflow-hidden bg-gw-card">
        <SampleBadge show={p.is_sample} />
        {p.images?.[0] ? (
          <img src={imgSrc(p.images[0])} alt={p.title} loading="lazy"
            className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${large ? "aspect-square" : "aspect-[2/1]"}`} />
        ) : (
          <div className={`flex items-end p-5 ${large ? "aspect-square" : "aspect-[2/1]"}`}><span className="lab text-gw-ink">Listing photo</span></div>
        )}
        <span className="lab absolute bottom-3 right-3 bg-gw-ground text-white px-2 py-1">{p.listing_type}</span>
        {p.international && (
          <span className="lab absolute bottom-3 left-3 bg-white/90 text-gw-onwhite px-2 py-1 inline-flex items-center gap-1.5" data-testid={`property-card-intl-${idx}`}>
            <Globe size={12} /> {p.country || "International"}
          </span>
        )}
        <FavButton id={p.id} title={p.title} testId={`property-card-fav-${idx}`}
          className="absolute top-3 right-3 z-10 w-9 h-9 bg-white/90 text-gw-onwhite" />
      </figure>
      <div className="flex items-start justify-between gap-4 mt-3">
        <p className="lux text-2xl">{p.title}</p>
        {p.price && <Price price={p.price} className="lab mt-2 text-gw-accent shrink-0" testId={`property-card-price-${idx}`} />}
      </div>
      <p className="text-sm text-gw-accent/80">{[p.location, p.bedrooms ? `${p.bedrooms} bed` : null, p.area].filter(Boolean).join(" · ")}</p>
      <span className="lab text-gw-accent inline-flex gap-2 mt-2">Enquire <span className="transition-transform duration-200 group-hover:translate-x-1">→</span></span>
    </Link>
  </article>
);

export const ProjectCard = ({ p, idx, tall = false }) => (
  <article className={`group ${idx % 2 === 1 ? "md:mt-24" : ""}`} data-testid={`project-card-${idx}`}>
    <Link to={`/development/${p.id}`} className="block" data-testid={`project-card-link-${idx}`}>
      <figure className="relative overflow-hidden bg-gw-mid">
        <SampleBadge show={p.is_sample} />
        {p.images?.[0] ? (
          <img src={imgSrc(p.images[0])} alt={p.title} loading="lazy"
            className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${tall || idx % 2 === 1 ? "aspect-[3/4]" : "aspect-[4/3]"}`} />
        ) : (
          <div className="aspect-[4/3] flex items-end p-5"><span className="lab text-gw-tint">Render</span></div>
        )}
        <span className="lab absolute bottom-3 left-3 bg-white text-gw-onwhite px-2 py-1">{p.status}</span>
      </figure>
      <p className="lux text-2xl mt-3">{p.title}</p>
      <p className="text-sm text-gw-pale">{[p.category, p.location, p.units].filter(Boolean).join(" · ")}</p>
      <span className="lab inline-flex gap-2 mt-2">View project <span className="transition-transform duration-200 group-hover:translate-x-1">→</span></span>
    </Link>
  </article>
);

export const Odometer = ({ value }) => {
  const digits = String(Math.min(value, 99)).padStart(2, "0").split("");
  return (
    <p className="gw-big lux" aria-label={`${value}`} data-testid="projects-count">
      {digits.map((d, i) => (
        <span key={i} className="odo">
          <span className={`reel ${i ? "r2" : ""}`} style={{ "--d": d }}>
            {"0123456789".split("").map((n) => <span key={n} className="block">{n}</span>)}
          </span>
        </span>
      ))}
    </p>
  );
};
